import React, { useState, useEffect, useMemo } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { ListGroup, Button, Alert, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaAddressBook } from 'react-icons/fa';
import './ContactList.css';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import Contact from '../../models/Contact';
import { buildContactsPageQuery, deleteContact } from '../../services/contactsService';
import { groupContacts } from '../../services/contactListGroups';
import ContentUnitTag from '../common/ContentUnitTag';
import { primaryContactEmail } from '../../platform/contactEmails';
import { primaryContactPhone } from '../../platform/contactPhones';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';

function formatDate(date) {
  if (!date) return '';
  const value = date instanceof Date ? date : new Date(date);
  return value.toLocaleString('sl-SI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ContactListItem({
  contact,
  groupId,
  isAdmin,
  onSelectContact,
  onEditContact,
  onDelete,
}) {
  const primaryEmail = primaryContactEmail(contact.emails);
  const primaryPhone = primaryContactPhone(contact.phones);

  return (
    <ListGroup.Item
      className="contact-list-item platform-content-list-item"
      onClick={() => onSelectContact?.(contact)}
    >
      <div
        className={`platform-content-list-item-main${isAdmin ? ' platform-content-list-item-main--has-actions' : ''}`}
      >
        <div className="platform-content-list-item-content">
          <div className="platform-content-list-item-thumb platform-content-list-item-thumb--icon">
            <div className="platform-content-list-item-thumb-placeholder" aria-hidden="true">
              <FaAddressBook />
            </div>
          </div>

          <div className="platform-content-list-item-body">
            <div className="platform-content-list-item-head">
              <div className="platform-content-list-item-title">{contact.name}</div>
            </div>
            {groupId === 'units' && (
              <div className="platform-content-list-item-badges">
                <ContentUnitTag item={contact} />
              </div>
            )}
            <div className="platform-content-list-item-footer contact-list-item-footer">
              {primaryEmail && (
                <>
                  <span>
                    <a
                      href={`mailto:${primaryEmail}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {primaryEmail}
                    </a>
                  </span>
                  <span className="platform-content-list-item-separator">·</span>
                </>
              )}
              {primaryPhone && (
                <>
                  <span>
                    <a
                      href={`tel:${primaryPhone}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {primaryPhone}
                    </a>
                  </span>
                  <span className="platform-content-list-item-separator">·</span>
                </>
              )}
              <span>{formatDate(contact.createdAt)}</span>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="platform-content-list-item-aside">
            <div className="platform-content-list-item-actions">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditContact(contact);
                }}
              >
                <FaEdit /> Uredi
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(contact.id);
                }}
              >
                <FaTrash /> Izbriši
              </Button>
            </div>
          </div>
        )}
      </div>
    </ListGroup.Item>
  );
}

function ContactList({ isAdmin, onEditContact, onSelectContact }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { user, loading: userLoading } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();

  useEffect(() => {
    if (userLoading || !user || !tenantId) return undefined;

    setLoading(true);
    const listQuery = buildContactsPageQuery(user, tenantId, memberships);
    const unsubscribe = onSnapshot(
      listQuery,
      (querySnapshot) => {
        setContacts(
          querySnapshot.docs.map((docSnap) => Contact.fromFirestore(docSnap)),
        );
        setLoading(false);
        setError('');
      },
      (err) => {
        console.error('Error fetching contacts:', err);
        setError('Napaka pri nalaganju kontaktov. Poskusite znova.');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user, userLoading, tenantId, memberships]);

  const groupedContacts = useMemo(
    () => groupContacts(contacts, config),
    [contacts, config],
  );

  const confirm = useConfirm();
  const toast = useToast();

  const handleDelete = async (contactId) => {
    const confirmed = await confirm({
      title: 'Izbriši kontakt',
      message: 'Ali ste prepričani, da želite izbrisati ta kontakt?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteContact(contactId);
      toast.deleted('Kontakt je bil izbrisan.');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Kontakta ni bilo mogoče izbrisati.');
    }
  };

  if (loading || userLoading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!groupedContacts.length) {
    return <div className="platform-content-list-empty">Ni kontaktov za prikaz.</div>;
  }

  return (
    <div className="contact-list-wrapper platform-content-list-wrapper">
      <div className="contact-list-groups">
        {groupedContacts.map(({ group, contacts: groupContacts }) => (
          <section key={group.id} className="contact-list-group">
            <h2 className="platform-section-title">{group.label}</h2>
            <ListGroup className="contact-list platform-content-list">
              {groupContacts.map((contact) => (
                <ContactListItem
                  key={contact.id}
                  contact={contact}
                  groupId={group.id}
                  isAdmin={isAdmin}
                  onSelectContact={onSelectContact}
                  onEditContact={onEditContact}
                  onDelete={handleDelete}
                />
              ))}
            </ListGroup>
          </section>
        ))}
      </div>
    </div>
  );
}

export default ContactList;
